// /settings/ai/openrouter — OpenRouter sends the browser back here after sign-in with ?code=&state=.
// We trade the code for the tenant's key on the server, then return to Settings → AI models.

import { buttonClass, Callout, Card, Spinner, useToast } from "@ml/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useDocumentTitle } from "../../lib/hooks";
import { aiApi, useAiMutation } from "./aiApi";

const BACK = "/settings?tab=ai";

export function OpenRouterCallbackPage() {
  useDocumentTitle("Connecting OpenRouter");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const finish = useAiMutation(({ code, state }: { code: string; state: string }) => aiApi.finishOAuth(code, state));
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      setError("OpenRouter didn't send a connection code back — the sign-in was probably cancelled.");
      return;
    }
    finish
      .mutateAsync({ code, state })
      .then(() => {
        toast.success("OpenRouter connected", "Your AI now runs on your own account. Pick your models below.");
        navigate(BACK, { replace: true });
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Couldn't finish connecting OpenRouter."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="gfp-page">
      <Card title="Connecting OpenRouter">
        {error ? (
          <Callout tone="danger" title="That didn't connect" action={<Link to={BACK} className={buttonClass({ variant: "primary", size: "sm" })}>Back to AI settings</Link>}>
            {error}
          </Callout>
        ) : (
          <p className="gf-row">
            <Spinner size={18} /> Finishing the connection with OpenRouter…
          </p>
        )}
      </Card>
    </div>
  );
}
